import { useQuery } from "@tanstack/react-query";
import { CheckIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import type { InventoryLotListRow } from "@/modules/inventory/domain/types";
import {
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@/shared/forms";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { Modal } from "@/shared/ui/Modal";
import { inventoryLotsApi } from "../api/client";
import { useCreateAdjustment } from "../api/queries";
import { baseQtyHint } from "../lib/qty-unit-hint";

const ADJUSTMENT_REASON_OPTIONS = [
  { value: "MANUAL_CORRECTION", label: "ການແກ້ໄຂດ້ວຍຕົນເອງ" },
  { value: "STOCK_COUNT", label: "ການນັບສິນຄ້າຄັ້ງ" },
  { value: "OTHER", label: "ອື່ນໆ" },
] as const;

const AdjustmentFormSchema = z.object({
  branchId: z.string().min(1, "ຕ້ອງເລືອກສາຂາ"),
  lotId: z.string().min(1, "ຕ້ອງເລືອກເລກ lot"),
  qtyDelta: z.coerce.number().refine((v) => v !== 0, "ປະລິມານຕ້ອງບໍ່ເທົ່າກັບ 0"),
  adjustmentReason: z.string().optional(),
  adjustmentNote: z.string().optional(),
});

type AdjustmentFormValues = z.infer<typeof AdjustmentFormSchema>;

type AdjustmentModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function AdjustmentModal({ open, onOpenChange }: AdjustmentModalProps) {
  const createAdjustment = useCreateAdjustment();

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
    enabled: open,
  });

  const branches: BranchRow[] = branchesData?.data ?? [];
  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: b.id, label: b.name })),
    [branches],
  );

  const methods = RHF.useForm<AdjustmentFormValues>({
    resolver: zodResolver(
      AdjustmentFormSchema,
    ) as RHF.Resolver<AdjustmentFormValues>,
    defaultValues: {
      branchId: "",
      lotId: "",
      qtyDelta: 0,
      adjustmentReason: ADJUSTMENT_REASON_OPTIONS[0].value,
      adjustmentNote: "",
    },
  });

  const branchId = methods.watch("branchId");
  const lotId = methods.watch("lotId");
  const qtyDelta = methods.watch("qtyDelta");

  const { data: selectedLot } = useQuery({
    queryKey: ["inventory", "lots", "lookup", "preload", lotId],
    queryFn: () => inventoryLotsApi.getForLookup(lotId),
    enabled: open && !!lotId,
  });

  const lotLookupQueryFn = useMemo(
    () =>
      ({ search, pageParam }: { search: string; pageParam: number }) =>
        branchId
          ? inventoryLotsApi.lookup({
              branchId,
              search: search || undefined,
              page: pageParam,
              limit: 20,
            })
          : Promise.resolve({ items: [], nextPage: null }),
    [branchId],
  );

  const lotPreloadQueryFn = useMemo(
    () => (id: string) => inventoryLotsApi.getForLookup(id),
    [],
  );

  useEffect(() => {
    if (open) {
      methods.reset({
        branchId: "",
        lotId: "",
        qtyDelta: 0,
        adjustmentReason: ADJUSTMENT_REASON_OPTIONS[0].value,
        adjustmentNote: "",
      });
    }
  }, [open, methods.reset]);

  useEffect(() => {
    methods.setValue("lotId", "");
  }, [branchId, methods.setValue]);

  const onSubmit = async (values: AdjustmentFormValues) => {
    try {
      await createAdjustment.mutateAsync({
        lotId: values.lotId,
        qtyDelta: values.qtyDelta,
      });
      toast.success("ບັນທຶກການປັບຍອດສຳເລັດ");
      onOpenChange(false);
    } catch (e: unknown) {
      const err = e as { message?: string; response?: { status?: number } };
      if (err.response?.status === 422) {
        toast.error("ຍອດຄົງເຫຼືອຈະຕິດລົບ – ກະລຸນາກວດສອບປະລິມານ");
        return;
      }
      toast.error(err?.message ?? "ບັນທຶກລົ້ມເຫຼວ");
    }
  };

  const factor =
    selectedLot?.receiveToBase != null && selectedLot.receiveToBase > 0
      ? selectedLot.receiveToBase
      : 1;
  const entryUnit = selectedLot?.receiveUnit || selectedLot?.baseUnit || "";
  const currentStockBase = selectedLot?.qtyRemaining ?? 0;
  const currentStock = currentStockBase / factor;
  const qtyDeltaNum =
    typeof qtyDelta === "number" ? qtyDelta : Number(qtyDelta) || 0;
  const newBalance = currentStock + qtyDeltaNum;
  const showProjectedImpact = !!lotId && selectedLot != null;
  const baseHint = baseQtyHint(qtyDeltaNum, factor, selectedLot?.baseUnit);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ສ້າງການປັບສະຕ໋ອກດ້ວຍຕົນເອງ"
      description="ບັນທຶກການປ່ຽນແປງສະຕ໋ອກທີ່ບໍ່ແມ່ນມາດຕະຖານເພື່ອການກວດສອບ"
      size="sm"
    >
      <FormRoot<AdjustmentFormValues>
        methods={methods as RHF.UseFormReturn<AdjustmentFormValues>}
        onSubmit={onSubmit}
        className="space-y-4"
      >
        <FormSelect
          name="branchId"
          label="ສາຂາເປົ້າຫມາຍ"
          options={branchOptions}
          placeholder="ເລືອກສາຂາ"
          requiredMark
        />
        <FormInfiniteCombobox<InventoryLotListRow>
          name="lotId"
          label="ເລກ Lot"
          hint={
            <span className="text-muted-foreground text-xs uppercase">
              ກັ້ນຕາມສາຂາ
            </span>
          }
          queryKey={["inventory", "lots", "lookup", branchId ?? ""]}
          queryFn={lotLookupQueryFn}
          preloadQueryFn={lotPreloadQueryFn}
          getLabel={(l) => {
            const lotFactor =
              l.receiveToBase != null && l.receiveToBase > 0
                ? l.receiveToBase
                : 1;
            const unit = l.receiveUnit || l.baseUnit || "";
            const qty = l.qtyRemaining / lotFactor;
            return `${l.itemName} (Lot #${l.lotNo}) – ${qty.toLocaleString("lo-LA", { maximumFractionDigits: 2 })} ${unit}`;
          }}
          getValue={(l) => l.id}
          placeholder="ເລືອກເລກ lot"
          requiredMark
        />
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            name="qtyDelta"
            label={entryUnit ? `ປະລິມານປັບ (${entryUnit})` : "ປະລິມານປັບ"}
            type="number"
            step="any"
            placeholder="ຕົວຢ່າງ: -2"
            requiredMark
            hint={
              baseHint
                ? `= ${baseHint} · ໃຊ້ຄ່າລົບສຳລັບການນຳອອກ`
                : "ໃຊ້ຄ່າລົບສຳລັບການນຳອອກ"
            }
          />
          <FormSelect
            name="adjustmentReason"
            label="ເຫດຜົນການປັບຍອດ"
            options={ADJUSTMENT_REASON_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            placeholder="ເລືອກເຫດຜົນ"
          />
        </div>
        {showProjectedImpact && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-800 dark:bg-blue-950/30">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 text-blue-600 text-sm dark:text-blue-400">
                ℹ
              </span>
              <div>
                <p className="font-medium text-sm">ຜົນກະທົບທີ່ຄາດຄິດ</p>
                <p className="text-muted-foreground text-sm">
                  ປັດຈຸບັນ:{" "}
                  <strong className="text-foreground">
                    {currentStock.toLocaleString("lo-LA", {
                      maximumFractionDigits: 2,
                    })}{" "}
                    {entryUnit}
                  </strong>
                  {" → "}
                  ຍອດໃໝ່:{" "}
                  <strong className="text-foreground">
                    {newBalance.toLocaleString("lo-LA", {
                      maximumFractionDigits: 2,
                    })}{" "}
                    {entryUnit}
                  </strong>
                </p>
              </div>
            </div>
          </div>
        )}
        <FormTextarea
          name="adjustmentNote"
          label="ຫມາຍເຫດການປັບຍອດ"
          placeholder="ອະທິບາຍເຫດຜົນທີ່ປັບຍອດ..."
          className="min-h-20 resize-none"
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="submit"
            disabled={createAdjustment.isPending}
            className="gap-2"
          >
            <CheckIcon className="size-4" />
            {createAdjustment.isPending ? "ກຳລັງປະຕິບັດ..." : "ປະຕິບັດການປັບຍອດ"}
          </Button>
        </div>
      </FormRoot>
    </Modal>
  );
}
